import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import { SmallBookAd } from '../components/Ads/BookAdvertisements';

const QuoteGalleryPage = ({ showToast }) => {
  const { token } = useAuth();
  const [jokes, setJokes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [shareModal, setShareModal] = useState(null);
  const [votedIds, setVotedIds] = useState(new Set());
  const [voteCounts, setVoteCounts] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const perPage = 12;

  const fetchJokes = useCallback(() => {
    setLoading(true);
    const catParam = selectedCategory ? `&category=${encodeURIComponent(selectedCategory)}` : '';
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/jokes/all?page=${page}&per_page=${perPage}${catParam}`, true);
    x.onload = function () {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          setJokes(data.jokes || []);
          setTotal(data.total || 0);
          setCategories(data.categories || []);
        } catch (e) {}
      }
      setLoading(false);
    };
    x.onerror = () => setLoading(false);
    x.send();
  }, [page, selectedCategory]);

  useEffect(() => { fetchJokes(); }, [fetchJokes]);

  // Fetch user's votes and leaderboard
  useEffect(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/quote-votes/my-votes`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          setVotedIds(new Set(data.voted_joke_ids || []));
        } catch (e) {}
      }
    };
    x.send();

    const lb = new XMLHttpRequest();
    lb.open('GET', `${API}/quote-votes/leaderboard?limit=10`, true);
    lb.onload = function () {
      if (lb.status === 200) {
        try {
          const data = JSON.parse(lb.responseText);
          setLeaderboard(data.leaderboard || []);
          const counts = {};
          (data.leaderboard || []).forEach(item => { counts[item.joke_id] = item.votes; });
          setVoteCounts(counts);
        } catch (e) {}
      }
    };
    lb.send();
  }, [token]);

  const handleVote = (jokeId) => {
    if (!token) { showToast && showToast('Please log in to vote', 'warning'); return; }
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/quote-votes/vote`, true);
    x.setRequestHeader('Content-Type', 'application/json');
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          setVotedIds(prev => {
            const next = new Set(prev);
            if (data.action === 'voted') next.add(jokeId);
            else next.delete(jokeId);
            return next;
          });
          setVoteCounts(prev => ({ ...prev, [jokeId]: data.vote_count }));
        } catch (e) {}
      }
    };
    x.send(JSON.stringify({ joke_id: jokeId }));
  };

  const catColors = {
    'Fighter Pilot': '#60a5fa', 'Survival': '#f87171', 'Military': '#34d399',
    'Letters to Evelyn': '#047857', 'Technology': '#22d3ee', 'Existential': '#fbbf24',
    'Family': '#fb923c', 'Bureaucracy': '#047857', 'Cosmic': '#818cf8',
    'InfoPilot': '#FEF08A', 'Observational': '#94a3b8', 'Book Excerpt': '#e879f9',
    'Aviation Heritage': '#38bdf8', 'Career Sabotage': '#ef4444', 'Book Promo': '#4ade80',
  };
  const catIcons = {
    'Fighter Pilot': 'fa-fighter-jet', 'Survival': 'fa-shield-alt', 'Military': 'fa-medal',
    'Letters to Evelyn': 'fa-envelope-open-text', 'Technology': 'fa-microchip',
    'Existential': 'fa-infinity', 'Family': 'fa-home', 'Bureaucracy': 'fa-landmark',
    'Cosmic': 'fa-rocket', 'InfoPilot': 'fa-search', 'Observational': 'fa-eye',
    'Book Excerpt': 'fa-book-open', 'Aviation Heritage': 'fa-plane', 'Career Sabotage': 'fa-fire',
    'Book Promo': 'fa-star',
  };
  const getColor = (cat) => catColors[cat] || '#047857';
  const getIcon = (cat) => catIcons[cat] || 'fa-quote-right';

  const shareToTwitter = (joke) => {
    const t = encodeURIComponent(`"${joke.text}"\n\n\u2014 John Selman, Letters to Evelyn\nhttps://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US`);
    window.open(`https://twitter.com/intent/tweet?text=${t}`, '_blank');
    setShareModal(null);
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent('https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    setShareModal(null);
  };

  const copyToClipboard = async (joke) => {
    const text = `"${joke.text}"\n\n\u2014 John Selman, Letters to Evelyn\nGet the book: https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US`;
    try {
      await navigator.clipboard.writeText(text);
      showToast && showToast('Quote copied!', 'success');
    } catch (e) {}
    setShareModal(null);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div data-testid="quote-gallery-page">
      {/* Hero Header */}
      <div className="card" style={{ marginBottom: 16, background: 'linear-gradient(135deg, rgba(168,85,250,0.15), rgba(244,114,182,0.1))', border: '1px solid rgba(168,85,250,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#0F172A', margin: 0 }}>
              <i className="fas fa-book-reader" style={{ color: '#047857' }} />
              Author Quotes Library
            </h2>
            <p style={{ color: '#a1a1aa', fontSize: '0.88rem', marginTop: 6 }}>
              {total} belly-laugh-inducing quotes from "Letters to Evelyn" by John Selman
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowLeaderboard(!showLeaderboard)}
              className={showLeaderboard ? 'btn btn-primary' : 'btn'}
              style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', padding: '8px 14px', fontSize: '0.85rem' }}
              data-testid="leaderboard-toggle-btn">
              <i className="fas fa-trophy" /> Leaderboard
            </button>
            <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US"
              target="_blank" rel="noopener noreferrer" className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
              data-testid="buy-book-btn">
              <i className="fas fa-shopping-cart" /> Get the Book
            </a>
          </div>
        </div>
      </div>

      {/* Category Filter Pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button data-testid="cat-filter-all"
          onClick={() => { setSelectedCategory(null); setPage(1); }}
          className={`btn ${!selectedCategory ? 'btn-primary' : ''}`}
          style={{ padding: '7px 14px', fontSize: '0.82rem', borderRadius: 20,
            ...(!selectedCategory ? {} : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#d4d4d8' })
          }}>
          All ({total || '...'})
        </button>
        {categories.map(cat => (
          <button key={cat} data-testid={`cat-filter-${cat}`}
            onClick={() => { setSelectedCategory(cat); setPage(1); }}
            style={{
              padding: '7px 14px', fontSize: '0.82rem', borderRadius: 20, cursor: 'pointer',
              background: selectedCategory === cat ? `${getColor(cat)}25` : 'rgba(255, 255, 255, 0.85)',
              border: `1px solid ${selectedCategory === cat ? getColor(cat) : 'rgba(255,255,255,0.1)'}`,
              color: selectedCategory === cat ? getColor(cat) : '#a1a1aa',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <i className={`fas ${getIcon(cat)}`} style={{ fontSize: '0.72rem' }} /> {cat}
          </button>
        ))}
      </div>

      {/* Leaderboard Panel */}
      {showLeaderboard && (
        <div className="card" data-testid="leaderboard-panel" style={{ marginBottom: 16, background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(168,85,250,0.1))', border: '1px solid rgba(251,191,36,0.25)' }}>
          <h3 style={{ color: '#047857', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-trophy" /> Community Favorites — Top Voted Quotes
          </h3>
          {leaderboard.length === 0 ? (
            <p style={{ color: '#71717a', textAlign: 'center', padding: 20 }}>No votes yet. Be the first to vote for your favorite quote!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {leaderboard.map((item, idx) => {
                const joke = jokes.find(j => j.id === item.joke_id) || {};
                const medalColors = ['#fbbf24', '#d1d5db', '#cd7f32'];
                return (
                  <div key={item.joke_id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: 'rgba(255, 255, 255, 0.85)', borderRadius: 10,
                    border: idx < 3 ? `1px solid ${medalColors[idx]}40` : '1px solid rgba(255, 255, 255, 0.85)',
                  }}>
                    <span style={{ fontSize: '1.2rem', fontWeight: 800, color: idx < 3 ? medalColors[idx] : '#71717a', minWidth: 30, textAlign: 'center' }}>
                      #{idx + 1}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#334155', fontSize: '0.88rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {joke.text || item.joke_id}
                      </p>
                    </div>
                    <span style={{ color: '#047857', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="fas fa-heart" /> {item.votes}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Quote Grid */}
      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 50 }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ color: '#a1a1aa', marginTop: 16 }}>Loading quotes...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {jokes.map((joke, idx) => {
            const color = getColor(joke.category);
            return (
              <div key={joke.id || idx} data-testid={`quote-card-${idx}`}
                style={{
                  background: '#1e293b', borderRadius: 16, padding: 20,
                  border: `1px solid ${color}30`, transition: 'transform 0.2s, border-color 0.2s',
                  cursor: 'default', display: 'flex', flexDirection: 'column',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = color; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = `${color}30`; }}>
                {/* Category Badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ background: `${color}20`, color, padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className={`fas ${getIcon(joke.category)}`} style={{ fontSize: '0.65rem' }} /> {joke.category}
                  </span>
                </div>
                {/* Quote Text */}
                <p style={{ color: '#334155', fontSize: '1rem', lineHeight: 1.7, fontStyle: 'italic', flex: 1, margin: '0 0 14px' }}>
                  <span style={{ fontSize: '1.5rem', color: `${color}80`, lineHeight: 0.8 }}>"</span>
                  {joke.text}
                  <span style={{ fontSize: '1.5rem', color: `${color}80`, lineHeight: 0.8 }}>"</span>
                </p>
                {/* Footer with Vote + Share */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button data-testid={`vote-quote-${idx}`}
                      onClick={() => handleVote(joke.id)}
                      style={{
                        background: votedIds.has(joke.id) ? 'rgba(239,68,68,0.15)' : 'rgba(255, 255, 255, 0.85)',
                        border: votedIds.has(joke.id) ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(255,255,255,0.1)',
                        color: votedIds.has(joke.id) ? '#ef4444' : '#71717a',
                        padding: '6px 12px', borderRadius: 20, fontSize: '0.78rem',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                        transition: 'all 0.2s',
                      }}>
                      <i className={votedIds.has(joke.id) ? 'fas fa-heart' : 'far fa-heart'} />
                      {voteCounts[joke.id] || 0}
                    </button>
                    <span style={{ color: '#52525b', fontSize: '0.7rem' }}>{'\u2014'} John Selman</span>
                  </div>
                  <button data-testid={`share-quote-${idx}`}
                    onClick={() => setShareModal(joke)}
                    style={{ background: `${color}12`, border: `1px solid ${color}30`, color, padding: '6px 14px', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-share-alt" /> Share
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button className="btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }} data-testid="prev-page-btn">
            <i className="fas fa-chevron-left" /> Prev
          </button>
          <span style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
            Page {page} of {totalPages}
          </span>
          <button className="btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }} data-testid="next-page-btn">
            Next <i className="fas fa-chevron-right" />
          </button>
        </div>
      )}

      {/* Book Promo Footer with All Purchase Links */}
      <div style={{
        marginTop: 24, padding: 24,
        background: 'linear-gradient(135deg, rgba(244, 114, 182, 0.12), rgba(252, 211, 77, 0.12))',
        borderRadius: 16, border: '1px solid rgba(244, 114, 182, 0.2)',
      }}>
        <h3 style={{ color: '#047857', marginBottom: 4, textAlign: 'center' }}>
          <i className="fas fa-book" style={{ marginRight: 8 }} />Letters to Evelyn by John Selman
        </h3>
        <p style={{ color: '#a1a1aa', textAlign: 'center', fontSize: '0.78rem', marginBottom: 12 }}>
          19 Five-Star Reviews from Readers' Favorite | BlueInk Notable Book Award | Optioned for Film
        </p>
        <p style={{ color: '#d4d4d8', marginBottom: 14, fontSize: '0.92rem', textAlign: 'center', lineHeight: 1.6 }}>
          A supernatural thriller comedy true story memoir with science-fiction, fantasy, and romantic comedy elements. The son of an elite U.S. Navy fighter pilot who flew A-4 Skyhawks and A-7 Corsair IIs on 298 bombing missions over Vietnam tells his story of survival, justice, and revelation.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="buy-paperback-btn">
            <i className="fas fa-book" style={{ marginRight: 6 }} /> Paperback
          </a>
          <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US" target="_blank" rel="noopener noreferrer" className="btn" style={{ background: '#fbbf24', color: '#1a1a2e' }} data-testid="buy-kindle-btn">
            <i className="fas fa-tablet-alt" style={{ marginRight: 6 }} /> Kindle - $2.99
          </a>
          <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US" target="_blank" rel="noopener noreferrer" className="btn btn-secondary" data-testid="buy-hardcover-btn">
            <i className="fas fa-book-open" style={{ marginRight: 6 }} /> Hardcover
          </a>
          <a href="https://play.google.com/store/audiobooks/details/John_Selman_Letters_to_Evelyn?id=AQAAAEAq4giiMM" target="_blank" rel="noopener noreferrer" className="btn" style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid #34d399', color: '#34d399' }} data-testid="buy-audiobook-btn">
            <i className="fas fa-headphones" style={{ marginRight: 6 }} /> Audiobook
          </a>
          <a href="https://drive.google.com/file/d/1UhDwAs9VwUUoIinRNwnHFYZ0RlcvMBQI/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="btn" style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid #60a5fa', color: '#60a5fa' }} data-testid="read-free-btn">
            <i className="fas fa-gift" style={{ marginRight: 6 }} /> Read FREE
          </a>
        </div>
        {/* Social Media Links */}
        <div style={{ borderTop: '1px solid rgba(203, 213, 225, 0.8)', paddingTop: 14 }}>
          <p style={{ color: '#71717a', fontSize: '0.75rem', textAlign: 'center', marginBottom: 10 }}>
            Connect with John Selman | Top Pilot Enterprises, Inc.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://www.facebook.com/profile.php?id=61586878161817" target="_blank" rel="noopener noreferrer" style={{ color: '#4267B2', fontSize: '1.1rem' }} data-testid="social-facebook" title="Letters to Evelyn on Facebook">
              <i className="fab fa-facebook-square" />
            </a>
            <a href="https://www.instagram.com/jjspilot76" target="_blank" rel="noopener noreferrer" style={{ color: '#E1306C', fontSize: '1.1rem' }} data-testid="social-instagram" title="@jjspilot76 on Instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="https://www.linkedin.com/in/john-selman-aa048411" target="_blank" rel="noopener noreferrer" style={{ color: '#0A66C2', fontSize: '1.1rem' }} data-testid="social-linkedin" title="John Selman on LinkedIn">
              <i className="fab fa-linkedin" />
            </a>
            <a href="https://www.pinterest.com/selman24" target="_blank" rel="noopener noreferrer" style={{ color: '#E60023', fontSize: '1.1rem' }} data-testid="social-pinterest" title="John Selman on Pinterest">
              <i className="fab fa-pinterest" />
            </a>
            <a href="https://www.youtube.com/@johnselman4221" target="_blank" rel="noopener noreferrer" style={{ color: '#FF0000', fontSize: '1.1rem' }} data-testid="social-youtube" title="Letters to Evelyn on YouTube">
              <i className="fab fa-youtube" />
            </a>
            <a href="https://www.infopilotexplorer.biz" target="_blank" rel="noopener noreferrer" style={{ color: '#FCD34D', fontSize: '1.1rem' }} data-testid="social-website" title="InfoPilotExplorer.biz">
              <i className="fas fa-globe" />
            </a>
            <a href="https://www.letterstoevelynbyjohnselmanII.com" target="_blank" rel="noopener noreferrer" style={{ color: '#047857', fontSize: '1.1rem' }} data-testid="social-author-site" title="Read the book free">
              <i className="fas fa-book-reader" />
            </a>
          </div>
        </div>
      </div>

      <SmallBookAd variant={3} />

      {/* Share Modal */}
      {shareModal && (
        <div className="modal-overlay" onClick={() => setShareModal(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal" onClick={e => e.stopPropagation()}
            style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 20, padding: 28, maxWidth: 440, width: '90%', border: `2px solid ${getColor(shareModal.category)}60` }}>
            <h3 style={{ color: getColor(shareModal.category), marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-share-alt" /> Share This Quote
            </h3>
            <div style={{ background: '#475569', padding: 16, borderRadius: 12, marginBottom: 18 }}>
              <p style={{ color: '#0F172A', fontStyle: 'italic', marginBottom: 8, lineHeight: 1.6 }}>"{shareModal.text}"</p>
              <p style={{ color: '#a1a1aa', fontSize: '0.82rem' }}>{'\u2014'} John Selman, Letters to Evelyn</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn" onClick={() => shareToTwitter(shareModal)}
                style={{ background: '#1DA1F2', color: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <i className="fab fa-twitter" /> Share on Twitter
              </button>
              <button className="btn" onClick={shareToFacebook}
                style={{ background: '#4267B2', color: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <i className="fab fa-facebook" /> Share on Facebook
              </button>
              <button className="btn btn-secondary" onClick={() => copyToClipboard(shareModal)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <i className="fas fa-copy" /> Copy to Clipboard
              </button>
              <button className="btn" onClick={() => setShareModal(null)}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#a1a1aa' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteGalleryPage;
